import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { useLanguage } from '../../contexts/LanguageContext';
import { useFormatageDates } from '../../utils/dates';

/**
 * Le libellé d'une notification, en DEUX lignes — le fait, puis le contexte.
 *
 * 🔴 CE COMPOSANT EXISTE POUR QU'IL N'Y AIT QU'UNE ÉCRITURE. Le panneau de la
 * cloche et la page affichent la même notification ; deux mises en forme
 * finiraient par dire deux choses de la même donnée. *C'est la divergence de
 * trop — on en a fermé six.*
 *
 * 🔴 LE TITRE EST COMPOSÉ À PARTIR DES DONNÉES, JAMAIS DU TEXTE STOCKÉ. Le
 * stocké est en français et n'est qu'un repli ; le titre affiché se construit
 * du `type` et de `product_name`, donc suit la langue du lecteur.
 *
 * 🔴 ET LE CONTEXTE NE S'ANALYSE PLUS. L'écran RELISAIT le message français à
 * l'expression régulière pour en extraire le stock et le seuil, puis réémettait
 * une phrase traduite — trois motifs, deux replis, et un abandon silencieux si
 * rien ne correspondait. Le serveur sert désormais `contexte` :
 *   · présent  → une notification écrite depuis RU7-a′, phrase TRADUITE ;
 *   · absent   → une notification antérieure, message affiché TEL QUEL.
 * ⚠️ *On ne réécrit pas des données existantes pour un changement d'affichage* —
 * même arbitrage que « on ne renumérote pas un bon déjà envoyé ». Les deux
 * formes coexistent, et la charge utile les DISTINGUE au lieu de les deviner.
 */
export const titreDe = (notification, t) => {
  const nom = notification.product_name || '';
  switch (notification.type) {
    case 'low_stock':
      return t('notifications.titleLowStockShort', { productName: nom });
    case 'out_of_stock':
      return t('notifications.titleOutOfStock', { productName: nom });
    case 'order_received':
      return t('notifications.titleOrderReceived', { productName: nom });
    default:
      // Un type inconnu retombe sur le titre STOCKÉ : c'est son unique usage.
      return notification.title;
  }
};

export const contexteDe = (notification, t) => {
  if (notification.contexte) {
    return t('notifications.stockContext', {
      stock: notification.contexte.stock,
      seuil: notification.contexte.seuil,
    });
  }
  // Forme ancienne : la prose stockée, telle qu'elle a été écrite.
  return notification.message || '';
};

const LigneNotification = ({ notification, dense = false }) => {
  const { t } = useLanguage();
  const { formaterHorodatageRelatif } = useFormatageDates();
  const nonLue = !notification.is_read;

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, minWidth: 0 }}>
      {/* 🔴 LA PASTILLE À GAUCHE, ET ELLE OCCUPE SA PLACE MÊME ABSENTE : sans
          largeur réservée, les lignes lues et non lues ne s'alignent pas, et
          l'œil lit un décalage au lieu d'un état. */}
      <Box
        sx={{
          width: 8, height: 8, borderRadius: '50%', mt: 0.75, flexShrink: 0,
          backgroundColor: (theme) => (nonLue ? theme.palette.primary.main : 'transparent'),
        }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant={dense ? 'body2' : 'subtitle1'}
          fontWeight={nonLue ? 700 : 400}
          sx={{ overflowWrap: 'anywhere' }}
        >
          {titreDe(notification, t)}
        </Typography>
        {/* La seconde ligne, en gris : le contexte, jamais une répétition du
            titre. Le nom du produit est DANS le titre — le redire ici ferait
            perdre la seule ligne qui apporte un chiffre. */}
        <Typography variant="caption" color="text.secondary" display="block"
                    sx={{ overflowWrap: 'anywhere' }}>
          {contexteDe(notification, t)}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {/* ⚠️ MÊME FONCTION QUE LA PAGE — relatif en deçà de 24 h, absolu
              au-delà. Elle vit dans `utils/dates.js`, seul détenteur de la
              conversion de fuseau. */}
          {formaterHorodatageRelatif(notification.created_at)}
        </Typography>
      </Box>
    </Box>
  );
};

/** Le fond d'une ligne non lue — même teinte au panneau et à la page. */
export const fondNonLue = (nonLue) => (nonLue
  ? { backgroundColor: (theme) => alpha(theme.palette.primary.main,
                                        theme.palette.mode === 'dark' ? 0.13 : 0.08) }
  : {});

export default LigneNotification;
